import {
  Badge,
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  ClearRounded,
  CloseRounded,
  FilterListRounded
} from '@material-ui/icons';
import { Autocomplete, Pagination } from '@material-ui/lab';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import SwipeableViews from 'react-swipeable-views';
import { GetProjects } from '../../api/projects';
import { RootState } from '../../app/store';
import { ORDER } from '../../common/enum/order';
import DocMyTaskCard from '../../components/DocMyTaskCard';
import DocMyTaskHistoryCard from '../../components/DocMyTaskHistoryCard';
import EnhancedTableToolbar from '../../components/EnhancedTableToolbar';
import TabPanel, { a11yProps } from '../../components/TabPanel';
import getNotFoundText from '../../functions/getNotFoundText';
import { getCookie, updateCookie } from '../../functions/manageCookies';
import scrollToMe from '../../functions/scrollToMe';
import { useAppDispatch } from '../../hooks/useAppRudux';
import { useQueryStringNumber } from '../../hooks/useQueryString';
import Breadcrumb from '../Breadcrumb';
import { getAllMyTaskProjectsAsync } from '../Project/projectSlice';
import {
  getProjectDocumentWorkflowActionItemsAsync,
  getProjectDocumentWorkflowMyDocumentsAsync
} from '../ProjectDocumentWorkflow/projectDocumentWorkflowSlice';
import { getMyTaskHistoryAsync } from '../UserApproval/userApprovalSlice';

interface SelectFilter {
  projectIds: number[];
  purposeIds: number[];
}

interface MyTaskParam {
  projectIds: number[];
  purposeIds: number[];
  page: number;
  limit: number;
  search?: string | null;
}

export default function MyTask() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { user } = useSelector((state: RootState) => state.userProfile);
  const { allMyTaskProjects } = useSelector(
    (state: RootState) => state.projects
  );
  const { actionItems, myDocuments } = useSelector(
    (state: RootState) => state.projectDocumentWorkflows
  );
  const { myHistory: approvalHistory } = useSelector(
    (state: RootState) => state.userApprovals
  );
  const tabId = useQueryStringNumber({ query: 'tabId' });
  const today = new Date();
  const [tabValue, setTabValue] = useState<number>(0);

  const allMyTaskProjectsParams: GetProjects = {
    page: 0,
    limit: 10000,
    sortBy: 'projectNo',
    orderBy: ORDER.asc
  }; //TODO: Need to add new API for getting all my task project

  const pageActionItemKey = `[DOC]-userId${user?.id}-myTask-actionItems`;
  const pageMyDocumentKey = `[DOC]-userId${user?.id}-myTask-myDocuments`;
  const pageApprovalHistoryKey = `[DOC]-userId${user?.id}-myTask-approvalHistory`;
  const cookieActionItemParams: MyTaskParam | undefined =
    getCookie(pageActionItemKey);
  const cookieMyDocumentParams: MyTaskParam | undefined =
    getCookie(pageMyDocumentKey);
  const cookieApprovalHistoryParams: MyTaskParam | undefined = getCookie(
    pageApprovalHistoryKey
  );

  const defaultActionItemParams: MyTaskParam = {
    projectIds: [],
    purposeIds: [],
    page: 0,
    limit: 30,
    search: null
  };
  const defaultMyDocumentParams: MyTaskParam = {
    projectIds: [],
    purposeIds: [],
    page: 0,
    limit: 30,
    search: null
  };
  const defaultApprovalHistoryParams: MyTaskParam = {
    projectIds: [],
    purposeIds: [],
    page: 0,
    limit: 30,
    search: null
  };

  const [openActionItemDrawer, setOpenActionItemDrawer] =
    useState<boolean>(false);
  const [actionItemParams, setActionItemParams] = useState<MyTaskParam>({
    ...defaultActionItemParams,
    ...cookieActionItemParams,
    page: 0
  });
  const [selectActionItemFilters, setSelectActionItemFilters] =
    useState<SelectFilter>({ projectIds: [], purposeIds: [] });

  const [openMyDocumentDrawer, setOpenMyDocumentDrawer] =
    useState<boolean>(false);
  const [myDocumentParams, setMyDocumentParams] = useState<MyTaskParam>({
    ...defaultMyDocumentParams,
    ...cookieMyDocumentParams,
    page: 0
  });
  const [selectMyDocumentFilters, setSelectMyDocumentFilters] =
    useState<SelectFilter>({ projectIds: [], purposeIds: [] });

  const [openApprovalHistoryDrawer, setOpenApprovalHistoryDrawer] =
    useState<boolean>(false);
  const [approvalHistoryParams, setApprovalHistoryParams] =
    useState<MyTaskParam>({
      ...defaultApprovalHistoryParams,
      ...cookieApprovalHistoryParams,
      page: 0
    });
  const [selectApprovalHistoryFilters, setSelectApprovalHistoryFilters] =
    useState<SelectFilter>({ projectIds: [], purposeIds: [] });

  useEffect(() => {
    if (tabId) {
      setTabValue(tabId);
    } else {
      setTabValue(0);
    }
  }, [tabId]);

  useEffect(() => {
    dispatch(
      getProjectDocumentWorkflowActionItemsAsync({ ...actionItemParams, today })
    );
    dispatch(
      getProjectDocumentWorkflowMyDocumentsAsync({ ...myDocumentParams, today })
    );
    dispatch(getMyTaskHistoryAsync(approvalHistoryParams));
    if (allMyTaskProjects.length === 0)
      dispatch(getAllMyTaskProjectsAsync(allMyTaskProjectsParams));
    if (cookieActionItemParams) {
      const { projectIds, purposeIds } = cookieActionItemParams;
      setSelectActionItemFilters({
        ...selectActionItemFilters,
        projectIds,
        purposeIds
      });
    }
    if (cookieMyDocumentParams) {
      const { projectIds, purposeIds } = cookieMyDocumentParams;
      setSelectMyDocumentFilters({
        ...selectMyDocumentFilters,
        projectIds,
        purposeIds
      });
    }
    if (cookieApprovalHistoryParams) {
      const { projectIds, purposeIds } = cookieApprovalHistoryParams;
      setSelectApprovalHistoryFilters({
        ...selectApprovalHistoryFilters,
        projectIds,
        purposeIds
      });
    }
  }, []);

  function handleChange(event: React.ChangeEvent<{}>, newValue: number) {
    setTabValue(newValue);
    if (newValue === 0) {
      history.push(`/my-tasks`);
    } else {
      history.push(`/my-tasks?tabId=${newValue}`);
    }
  }

  function handleChangeIndex(index: number) {
    if (tabValue !== index) {
      setTabValue(index);
      if (index === 0) {
        history.push(`/my-tasks`);
      } else {
        history.push(`/my-tasks?tabId=${index}`);
      }
    }
  }

  function loadingActionItems(actionItemParams: MyTaskParam) {
    updateCookie(pageActionItemKey, actionItemParams);
    dispatch(
      getProjectDocumentWorkflowActionItemsAsync({ ...actionItemParams, today })
    );
    setActionItemParams(actionItemParams);
  }

  function loadingMyDocuments(myDocumentParams: MyTaskParam) {
    updateCookie(pageMyDocumentKey, myDocumentParams);
    dispatch(
      getProjectDocumentWorkflowMyDocumentsAsync({ ...myDocumentParams, today })
    );
    setMyDocumentParams(myDocumentParams);
  }

  function loadingApprovalHistory(approvalHistoryParams: MyTaskParam) {
    updateCookie(pageApprovalHistoryKey, approvalHistoryParams);
    dispatch(getMyTaskHistoryAsync(approvalHistoryParams));
    setApprovalHistoryParams(approvalHistoryParams);
  }

  function handleChangePage<T>(
    newPage: number,
    params: T,
    loadingElement: (params: T) => void
  ) {
    scrollToMe('tab-selections');
    loadingElement({
      ...params,
      page: newPage - 1 //The first value of pagination is 1, not 0
    });
  }

  return (
    <div className={classes.root}>
      <Breadcrumb name="myTask" />
      <Typography className={classes.title} component="p" variant="h4">
        {t('myTasks.title')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Tabs
            id="tab-selections"
            className={classes.tabs}
            value={tabValue}
            onChange={handleChange}
            indicatorColor="primary"
            textColor="primary"
            scrollButtons="auto"
          >
            <Tab
              label={
                <div className={classes.flexAlignCenter}>
                  <Typography variant="subtitle2">
                    {t('myTasks.tabs.waitForReview')}
                  </Typography>
                  {actionItems.count > 0 && (
                    <Chip
                      className={classes.styledBadge}
                      color="primary"
                      size="small"
                      label={actionItems.count}
                    />
                  )}
                </div>
              }
              {...a11yProps(0)}
            />
            <Tab
              label={
                <div className={classes.flexAlignCenter}>
                  <Typography variant="subtitle2">
                    {t('myTasks.tabs.waitForIssue')}
                  </Typography>
                  {myDocuments.count > 0 && (
                    <Chip
                      className={classes.styledBadge}
                      color="primary"
                      size="small"
                      label={myDocuments.count}
                    />
                  )}
                </div>
              }
              {...a11yProps(1)}
            />
            <Tab
              label={
                <div className={classes.flexAlignCenter}>
                  <Typography variant="subtitle2">
                    {t('myTasks.tabs.approvalHistory')}
                  </Typography>
                </div>
              }
              {...a11yProps(2)}
            />
          </Tabs>
          <SwipeableViews
            axis="x"
            index={tabValue}
            onChangeIndex={handleChangeIndex}
          >
            <TabPanel value={tabValue} index={0}>
              <div
                className={clsx(classes.flexAlignCenter, {
                  [classes.paddingRight1]:
                    actionItemParams.projectIds.length +
                      actionItemParams.purposeIds.length >
                    0
                })}
              >
                <div className={classes.flexGrow}>
                  <EnhancedTableToolbar<MyTaskParam>
                    name={t('myTasks.toolbarNames.actionItems')}
                    params={actionItemParams}
                    loadingElement={loadingActionItems}
                    canSearchable
                  />
                </div>
                <Tooltip title={t('common.filter') as string} arrow>
                  <Badge
                    badgeContent={
                      actionItemParams.projectIds.length +
                      actionItemParams.purposeIds.length
                    }
                    color="primary"
                    overlap="circle"
                  >
                    <IconButton
                      className={classes.marginBottom1}
                      size="small"
                      onClick={() => setOpenActionItemDrawer(true)}
                    >
                      <FilterListRounded />
                    </IconButton>
                  </Badge>
                </Tooltip>
              </div>
              <Typography
                className={classes.marginBottom1}
                variant="body2"
                color="textSecondary"
              >
                {t('common.about')} {actionItems.count}{' '}
                {actionItems.count > 1
                  ? t('common.results')
                  : t('common.result')}
              </Typography>
              {actionItems.data.length > 0 ? (
                <DocMyTaskCard renderMyTaskDocs={actionItems.data} />
              ) : (
                <div className={classes.fileNotFound}>
                  {getNotFoundText(
                    actionItemParams.search,
                    t('common.document') as string,
                    t('common.documents') as string,
                    t
                  )}
                </div>
              )}
              <div className={classes.pagination}>
                <Pagination
                  count={Math.ceil(actionItems.count / actionItemParams.limit)}
                  shape="rounded"
                  page={actionItemParams.page + 1}
                  onChange={(
                    event: React.ChangeEvent<unknown>,
                    newPage: number
                  ) => {
                    handleChangePage<MyTaskParam>(
                      newPage,
                      actionItemParams,
                      loadingActionItems
                    );
                  }}
                />
              </div>
              <Paper className={classes.card}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('myTasks.summaryReport.title')}
                </Typography>
                <Divider className={classes.divider} />
                <div className={classes.label}>
                  <div className={classes.divCount}>
                    <Chip
                      className={clsx(
                        classes.chipCount,
                        classes.chipCountDelay
                      )}
                      label={actionItems.countDelay}
                    />
                    <Typography>
                      {t('myTasks.summaryReport.overdue')}
                    </Typography>
                  </div>
                  <div className={classes.divCount}>
                    <Chip
                      className={clsx(
                        classes.chipCount,
                        classes.chipCountOnDue
                      )}
                      label={actionItems.countOnDue}
                    />
                    <Typography>{t('myTasks.summaryReport.onTime')}</Typography>
                  </div>
                </div>
              </Paper>
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <div
                className={clsx(classes.flexAlignCenter, {
                  [classes.paddingRight1]:
                    myDocumentParams.projectIds.length +
                      myDocumentParams.purposeIds.length >
                    0
                })}
              >
                <div className={classes.flexGrow}>
                  <EnhancedTableToolbar<MyTaskParam>
                    name={t('myTasks.toolbarNames.myDocuments')}
                    params={myDocumentParams}
                    loadingElement={loadingMyDocuments}
                    canSearchable
                  />
                </div>
                <Tooltip title={t('common.filter') as string} arrow>
                  <Badge
                    badgeContent={
                      myDocumentParams.projectIds.length +
                      myDocumentParams.purposeIds.length
                    }
                    color="primary"
                    overlap="circle"
                  >
                    <IconButton
                      className={classes.marginBottom1}
                      size="small"
                      onClick={() => setOpenMyDocumentDrawer(true)}
                    >
                      <FilterListRounded />
                    </IconButton>
                  </Badge>
                </Tooltip>
              </div>
              <Typography
                className={classes.marginBottom1}
                variant="body2"
                color="textSecondary"
              >
                {t('common.about')} {myDocuments.count}{' '}
                {myDocuments.count > 1
                  ? t('common.results')
                  : t('common.result')}
              </Typography>
              {myDocuments.data.length > 0 ? (
                <DocMyTaskCard renderMyTaskDocs={myDocuments.data} />
              ) : (
                <div className={classes.fileNotFound}>
                  {getNotFoundText(
                    myDocumentParams.search,
                    t('common.document') as string,
                    t('common.documents') as string,
                    t
                  )}
                </div>
              )}
              <div className={classes.pagination}>
                <Pagination
                  count={Math.ceil(myDocuments.count / myDocumentParams.limit)}
                  shape="rounded"
                  page={myDocumentParams.page + 1}
                  onChange={(
                    event: React.ChangeEvent<unknown>,
                    newPage: number
                  ) => {
                    handleChangePage<MyTaskParam>(
                      newPage,
                      myDocumentParams,
                      loadingMyDocuments
                    );
                  }}
                />
              </div>
              <Paper className={classes.card}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('myTasks.summaryReport.title')}
                </Typography>
                <Divider className={classes.divider} />
                <div className={classes.label}>
                  <div className={classes.divCount}>
                    <Chip
                      className={clsx(
                        classes.chipCount,
                        classes.chipCountDelay
                      )}
                      label={myDocuments.countDelay}
                    />
                    <Typography>
                      {t('myTasks.summaryReport.overdue')}
                    </Typography>
                  </div>
                  <div className={classes.divCount}>
                    <Chip
                      className={clsx(
                        classes.chipCount,
                        classes.chipCountOnDue
                      )}
                      label={myDocuments.countOnDue}
                    />
                    <Typography>{t('myTasks.summaryReport.onTime')}</Typography>
                  </div>
                </div>
              </Paper>
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              <div
                className={clsx(classes.flexAlignCenter, {
                  [classes.paddingRight1]:
                    approvalHistoryParams.projectIds.length +
                      approvalHistoryParams.purposeIds.length >
                    0
                })}
              >
                <div className={classes.flexGrow}>
                  <EnhancedTableToolbar<MyTaskParam>
                    name={t('myTasks.toolbarNames.approvalHistory')}
                    params={approvalHistoryParams}
                    loadingElement={loadingApprovalHistory}
                    canSearchable
                  />
                </div>
                <Tooltip title={t('common.filter') as string} arrow>
                  <Badge
                    badgeContent={
                      approvalHistoryParams.projectIds.length +
                      approvalHistoryParams.purposeIds.length
                    }
                    color="primary"
                    overlap="circle"
                  >
                    <IconButton
                      className={classes.marginBottom1}
                      size="small"
                      onClick={() => setOpenApprovalHistoryDrawer(true)}
                    >
                      <FilterListRounded />
                    </IconButton>
                  </Badge>
                </Tooltip>
              </div>
              <Typography
                className={classes.marginBottom1}
                variant="body2"
                color="textSecondary"
              >
                {t('common.about')} {approvalHistory.count}{' '}
                {approvalHistory.count > 1
                  ? t('common.results')
                  : t('common.result')}
              </Typography>
              {approvalHistory.data.length > 0 ? (
                <DocMyTaskHistoryCard renderMyHistory={approvalHistory.data} />
              ) : (
                <div className={classes.fileNotFound}>
                  {getNotFoundText(
                    approvalHistoryParams.search,
                    t('common.document') as string,
                    t('common.documents') as string,
                    t
                  )}
                </div>
              )}
              <div className={classes.pagination}>
                <Pagination
                  count={Math.ceil(
                    approvalHistory.count / approvalHistoryParams.limit
                  )}
                  shape="rounded"
                  page={approvalHistoryParams.page + 1}
                  onChange={(
                    event: React.ChangeEvent<unknown>,
                    newPage: number
                  ) => {
                    handleChangePage<MyTaskParam>(
                      newPage,
                      approvalHistoryParams,
                      loadingApprovalHistory
                    );
                  }}
                />
              </div>
            </TabPanel>
          </SwipeableViews>
        </Grid>
      </Grid>
      <DrawerMyTask<MyTaskParam>
        open={openActionItemDrawer}
        setOpen={setOpenActionItemDrawer}
        params={actionItemParams}
        selectFilter={selectActionItemFilters}
        setSelectFilter={setSelectActionItemFilters}
        loadingElement={loadingActionItems}
      />
      <DrawerMyTask<MyTaskParam>
        open={openMyDocumentDrawer}
        setOpen={setOpenMyDocumentDrawer}
        params={myDocumentParams}
        selectFilter={selectMyDocumentFilters}
        setSelectFilter={setSelectMyDocumentFilters}
        loadingElement={loadingMyDocuments}
      />
      <DrawerMyTask<MyTaskParam>
        open={openApprovalHistoryDrawer}
        setOpen={setOpenApprovalHistoryDrawer}
        params={approvalHistoryParams}
        selectFilter={selectApprovalHistoryFilters}
        setSelectFilter={setSelectApprovalHistoryFilters}
        loadingElement={loadingApprovalHistory}
      />
    </div>
  );
}

interface DrawerParamProps {
  projectIds: number[];
  purposeIds: number[];
  page: number;
  limit: number;
  search?: string | null;
}

interface DrawerProps<T> {
  open: boolean;
  setOpen: (open: boolean) => void;
  params: T;
  selectFilter: SelectFilter;
  setSelectFilter: (selectFilter: SelectFilter) => void;
  loadingElement: (params: T) => void;
}

function DrawerMyTask<T extends DrawerParamProps>({
  open,
  setOpen,
  params: drawerParams,
  selectFilter,
  setSelectFilter,
  loadingElement
}: DrawerProps<T>) {
  const classes = useDrawerStyles();
  const { t } = useTranslation();

  const { allMyTaskProjects } = useSelector(
    (state: RootState) => state.projects
  );
  const { data: purposes } = useSelector((state: RootState) => state.purposes);

  const [canApplyFilter, setCanApplyFilter] = useState<boolean>(false);

  const drawer = (
    <div role="presentation">
      <div className={classes.drawerHeader}>
        <Typography variant="h6">{t('myTasks.drawer.title')}</Typography>
        <Tooltip title={t('common.close') as string}>
          <IconButton onClick={handleFilterDrawer(false)}>
            <CloseRounded />
          </IconButton>
        </Tooltip>
      </div>
      <Divider />
      <div className={classes.drawerBody}>
        <Autocomplete
          className={classes.filterField}
          id="projects"
          options={allMyTaskProjects}
          multiple
          getOptionLabel={(option) =>
            `[ ${option?.projectNo} ] ${option?.name}`
          }
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                label={`[ ${option?.projectNo} ] ${option?.name}`}
                {...getTagProps({ index })}
                variant="outlined"
                size="small"
                deleteIcon={<ClearRounded />}
              />
            ))
          }
          value={(allMyTaskProjects || []).filter((project) =>
            selectFilter.projectIds.some(
              (projectId) => projectId === project.id
            )
          )}
          getOptionSelected={(option, value) => option.id === value.id}
          onChange={(event, newValue) => {
            setSelectFilter({
              ...selectFilter,
              projectIds: newValue.map((project) => project.id)
            });
            setCanApplyFilter(true);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder={
                selectFilter.projectIds?.length === 0
                  ? (t('myTasks.drawer.filterByProject') as string)
                  : ''
              }
            />
          )}
        />
        <Autocomplete
          className={classes.filterField}
          id="purpose"
          options={purposes}
          multiple
          getOptionLabel={(option) =>
            `${option?.abbreviation}: ${option?.fullName}`
          }
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                label={`${option?.abbreviation}`}
                {...getTagProps({ index })}
                variant="outlined"
                size="small"
                deleteIcon={<ClearRounded />}
              />
            ))
          }
          value={purposes.filter((purpose) =>
            selectFilter.purposeIds.some(
              (purposeId) => purposeId === purpose.id
            )
          )}
          getOptionSelected={(option, value) => option.id === value.id}
          onChange={(event, newValue) => {
            setSelectFilter({
              ...selectFilter,
              purposeIds: newValue.map((purpose) => purpose.id)
            });
            setCanApplyFilter(true);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder={
                selectFilter.purposeIds?.length === 0
                  ? (t('myTasks.drawer.filterByPurpose') as string)
                  : ''
              }
            />
          )}
        />
        <div className={classes.filterButton}>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={handleClearFilter}
          >
            {t('myTasks.drawer.clear')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={handleLoadingFilter}
            disabled={!canApplyFilter}
          >
            {t('myTasks.drawer.apply')}
          </Button>
        </div>
      </div>
    </div>
  );

  function handleFilterDrawer(open: boolean) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      setOpen(open);
    };
  }

  function handleLoadingFilter() {
    loadingElement({
      ...drawerParams,
      ...selectFilter,
      page: 0
    });
    setOpen(false);
    setCanApplyFilter(false);
  }

  function handleClearFilter() {
    loadingElement({
      ...drawerParams,
      projectIds: [],
      purposeIds: [],
      page: 0,
      search: null
    });
    setSelectFilter({ projectIds: [], purposeIds: [] });
    setOpen(false);
    setCanApplyFilter(false);
  }

  return (
    <>
      <Drawer
        className={classes.drawerDesktop}
        anchor="right"
        open={open}
        onClose={handleFilterDrawer(false)}
      >
        {drawer}
      </Drawer>
      <Drawer
        className={classes.drawerMobile}
        anchor="bottom"
        open={open}
        onClose={handleFilterDrawer(false)}
      >
        {drawer}
      </Drawer>
    </>
  );
}

const useDrawerStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawerDesktop: {
      display: 'block',
      [theme.breakpoints.down('xs')]: {
        display: 'none'
      },
      '& .MuiDrawer-paper': {
        boxSizing: 'border-box',
        width: 400,
        borderRadius: theme.spacing(2, 0, 0, 2)
      }
    },
    drawerMobile: {
      display: 'none',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      },
      '& .MuiDrawer-paper': {
        boxSizing: 'border-box',
        width: '100%',
        borderRadius: theme.spacing(2, 2, 0, 0)
      }
    },
    drawerHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(0, 1, 0, 3),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar
    },
    drawerBody: {
      padding: theme.spacing(1, 2, 2, 2),
      '& > *': {
        marginTop: theme.spacing(1)
      }
    },
    filterField: {
      width: '100%',
      marginBottom: theme.spacing(1)
    },
    filterButton: {
      '& > *': {
        marginTop: theme.spacing(1)
      }
    }
  })
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    tabs: {
      marginBottom: theme.spacing(1),
      borderBottomStyle: 'solid',
      borderWidth: '1px',
      borderColor: theme.palette.divider
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    flexGrow: {
      flexGrow: 1
    },
    fileNotFound: {
      padding: theme.spacing(10, 2),
      textAlign: 'center'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(4)
    },
    label: {
      display: 'flex',
      height: 40,
      alignItems: 'center'
    },
    styledBadge: {
      marginLeft: theme.spacing(1),
      minWidth: 24
    },
    chipCount: {
      margin: theme.spacing(0, 1, 0, 0),
      borderRadius: 4,
      color: 'white',
      fontSize: 18
    },
    chipCountDelay: { backgroundColor: theme.palette.error.main },
    chipCountOnDue: { backgroundColor: theme.palette.success.main },
    labelCountPaper: {
      display: 'flex',
      margin: theme.spacing(0, 1),
      width: 30,
      height: 30
    },
    labelCount: {
      display: 'flex',
      alignItems: 'center',
      width: 40,
      height: 40,
      borderRadius: 8,
      textAlign: 'center'
    },
    cardDocument: {
      margin: theme.spacing(1, 0, 1, 0)
    },
    divCount: {
      display: 'flex',
      alignItems: 'center',
      width: '50%'
    },
    marginBottom1: {
      marginBottom: theme.spacing(1)
    },
    paddingRight1: {
      paddingRight: theme.spacing(1)
    }
  })
);
